import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { readFileAsDataURL } from "@/lib/utils";
import { processDocument } from "@/api";
import { FileText, Image as ImageIcon, Images, Scissors, Loader2 } from "lucide-react";

const OPTIONS = [
  {
    key: "import_as_is",
    icon: FileText,
    label: "Import as is",
    desc: "Keep the original PDF in your library.",
  },
  {
    key: "single_image",
    icon: ImageIcon,
    label: "Convert to one image",
    desc: "All pages stitched into a single tall image.",
  },
  {
    key: "multi_image",
    icon: Images,
    label: "Convert to multiple images",
    desc: "Each page becomes its own image.",
  },
  {
    key: "extract_images",
    icon: Scissors,
    label: "Extract images from file",
    desc: "Pull out the images embedded inside the PDF.",
  },
];

/**
 * Dialog shown when one or more PDFs are uploaded. Lets the user pick any
 * combination of conversion actions (at least one required) before processing.
 *
 * Props:
 *   open, onOpenChange
 *   files        array of PDF File objects to process
 *   projectId
 *   category
 *   onProcessed(item)  called for every created library item
 */
export default function DocumentImportDialog({
  open,
  onOpenChange,
  files = [],
  projectId,
  category = "inspiration",
  onProcessed,
}) {
  const [selected, setSelected] = useState({ import_as_is: true });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Reset selection each time the dialog opens
  useEffect(() => {
    if (open) {
      setSelected({ import_as_is: true });
      setProgress({ done: 0, total: 0 });
    }
  }, [open]);

  const chosen = OPTIONS.filter((o) => selected[o.key]).map((o) => o.key);
  const canContinue = chosen.length > 0 && files.length > 0 && !busy;

  function toggle(key) {
    setSelected((s) => ({ ...s, [key]: !s[key] }));
  }

  async function handleContinue() {
    if (chosen.length === 0) {
      toast.error("Select at least one option");
      return;
    }
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    let totalItems = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const data = await readFileAsDataURL(f);
        const items = await processDocument(projectId, {
          project_id: projectId,
          data,
          filename: f.name,
          category,
          options: chosen,
        });
        (items || []).forEach((it) => onProcessed?.(it));
        totalItems += (items || []).length;
      } catch (e) {
        console.error(e);
        const msg = e?.response?.data?.detail || `Failed to process ${f.name}`;
        toast.error(typeof msg === "string" ? msg : `Failed to process ${f.name}`);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setBusy(false);
    if (totalItems > 0) {
      toast.success(`Added ${totalItems} item${totalItems === 1 ? "" : "s"} from PDF`);
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (busy ? null : onOpenChange(v))}>
      <DialogContent className="max-w-lg" data-testid="document-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            How should we import this PDF?
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            {files.length > 1
              ? `${files.length} PDF files · choose any combination of actions.`
              : "Choose any combination of actions. At least one is required."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-1">
          {OPTIONS.map((o) => {
            const active = !!selected[o.key];
            return (
              <label
                key={o.key}
                htmlFor={`doc-opt-${o.key}`}
                data-testid={`doc-option-${o.key}`}
                className={
                  "flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors " +
                  (active
                    ? "border-stone-900 bg-stone-50"
                    : "border-stone-200 hover:border-stone-300")
                }
              >
                <Checkbox
                  id={`doc-opt-${o.key}`}
                  checked={active}
                  onCheckedChange={() => toggle(o.key)}
                  className="mt-0.5"
                  data-testid={`doc-checkbox-${o.key}`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <o.icon className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
                    <span className="text-sm font-semibold text-stone-900">
                      {o.label}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">{o.desc}</p>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-stone-400" data-testid="doc-selected-count">
            {chosen.length} selected
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="rounded-full"
              data-testid="doc-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue}
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
              data-testid="doc-continue-button"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing {progress.done}/{progress.total}
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
